<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4 pb-3 border-bottom border-subtle d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          <i class="mdi mdi-shield-account text-primary mr-2"></i>{{ $t('squads.title') }}
        </h3>
        <p class="text-muted small mb-0">{{ $t('squads.subtitle') }}</p>
      </div>
      <div class="mt-3 mt-md-0">
        <router-link
          :to="{ name: 'CreateSquad' }"
          class="btn btn-primary btn-sm px-4 font-weight-bold d-inline-flex align-items-center"
        >
          <i class="mdi mdi-plus-circle mr-1"></i>
          {{ $t('squads.createSquad') }}
        </router-link>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <!-- Search & Filter Bar -->
          <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-2">
            <div class="search-box" style="max-width: 360px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('squads.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-bold">{{ filteredSquads.length }} {{ $t('nav.squads') }}</span>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 45px;">No</th>
                  <th style="width: 260px;">{{ $t('squads.squadName') }}</th>
                  <th style="width: 140px;">{{ $t('squads.membersCount') }}</th>
                  <th style="width: 150px;">{{ $t('squads.totalGold') }}</th>
                  <th style="width: 180px;">{{ $t('squads.performanceIndex') }}</th>
                  <th class="text-right" style="width: 110px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedSquads" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <router-link
                      :to="{ name: 'DetailSquad', params: { id: String(data.id) } }"
                      class="text-white font-weight-bold text-decoration-none d-flex align-items-center overflow-hidden"
                    >
                      <div
                        class="p-2 rounded mr-2 flex-shrink-0"
                        style="background: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.3);"
                      >
                        <i class="mdi mdi-shield-account text-primary"></i>
                      </div>
                      <div class="overflow-hidden">
                        <div class="text-truncate" :title="data.name">{{ data.name }}</div>
                        <small class="text-muted text-truncate d-block" v-if="data.code">{{ $currentLocale === 'id' ? 'Kode' : 'Code' }}: {{ data.code }}</small>
                      </div>
                    </router-link>
                  </td>
                  <td>
                    <span class="badge badge-outline-info font-weight-medium">
                      {{ data.users_count || data.players_count || data.members_count || 0 }} {{ $t('workspace.members') }}
                    </span>
                  </td>
                  <td>
                    <span class="text-warning font-weight-bold">
                      <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>{{ Number(data.total_gold || data.points || 0).toLocaleString($currentLocale === 'id' ? 'id-ID' : 'en-US') }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex justify-content-between small text-muted mb-1">
                      <span>{{ $t('squads.progressLabel') }}</span>
                      <span>{{ Number(data.progress_rate || 0) }}%</span>
                    </div>
                    <div class="progress" style="height: 6px;">
                      <div
                        class="progress-bar bg-primary"
                        role="progressbar"
                        :style="{ width: `${Math.min(100, Math.max(0, Number(data.progress_rate || 0)))}%` }"
                      ></div>
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailSquad', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditSquad', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button
                        type="button"
                        class="btn btn-action-icon btn-outline-danger"
                        @click="handleDelete(data.id)"
                        :title="$t('common.delete')"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedSquads.length === 0">
                  <td colspan="6" class="text-center py-5">
                    <EmptyState
                      icon="mdi-account-group-outline"
                      :title="$t('squads.emptyTitle')"
                      :description="$t('squads.emptyDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredSquads.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredSquads.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSquads, deleteSquad } from "../../services/squadService";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import Swal from "sweetalert2";

export default {
  name: "SquadView",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: "",
      squads: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
    };
  },
  computed: {
    filteredSquads() {
      if (!this.search) return this.squads;
      const q = this.search.toLowerCase();
      return this.squads.filter((item) => (item.name || "").toLowerCase().includes(q));
    },
    paginatedSquads() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredSquads.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.fetchSquads();
  },
  methods: {
    async fetchSquads() {
      this.loading = true;
      try {
        const response = await getSquads();
        this.squads = response.data || [];
      } catch (e) {
        // handled
      } finally {
        this.loading = false;
      }
    },
    async handleDelete(id) {
      const result = await Swal.fire({
        title: this.$t("common.confirm"),
        text: this.$t("squads.confirmDelete"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: this.$t("common.delete"),
        cancelButtonText: this.$t("common.cancel"),
        background: "#12141c",
        color: "#fff",
      });
      if (!result.isConfirmed) return;

      try {
        await deleteSquad(id);
        Swal.fire({
          icon: "success",
          title: this.$t("common.success"),
          text: this.$t("squads.deletedSuccess"),
          timer: 1500,
          showConfirmButton: false,
          background: "#12141c",
          color: "#fff",
        });
        await this.fetchSquads();
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: this.$currentLocale === 'id' ? "Gagal menghapus divisi." : "Failed to delete division.",
          background: "#12141c",
          color: "#fff",
        });
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>
